/**
 * DEVLOGS-TEMP — configuration resolution. Pure: env is an ARGUMENT.
 *
 * THE SWITCH IS THE URL. Not a boolean beside a URL — that is two sources of
 * truth and they drift. This is the house pattern already used twice:
 * `parseSentryDsn(...) -> null` makes the whole crash reporter inert, and
 * `logConfig.drainUrl && ...` gates the api-server log drain.
 *
 * PRODUCTION GUARD — read this before "simplifying" it away.
 * compose.yml pins `NODE_ENV: production` on BOTH droplets ("Non-negotiable in
 * this topology; wins over anything in .env"), so NODE_ENV cannot discriminate
 * dev from prod here: keying the guard on it would silently disable this
 * pipeline on the dev droplet, which is the one surface it exists for.
 * `isProduction` is therefore a BUILD-TIME fact the caller supplies
 * (`import.meta.env.PROD`, `Updates.channel`, `app.isPackaged`) and is left
 * `false` on the server, where the real guards are: the URL is never in the
 * production env file, and the Worker rejects records whose `env` is not "dev".
 */

import { LEVELS, parseLevel, parseNamespaceFilter } from "./level.ts";
import type { NamespaceFilter } from "./level.ts";
import type { DevLogSurface, Level } from "./types.ts";

export interface EnvRecord {
  readonly [key: string]: string | undefined;
}

export interface HostFacts {
  surface: DevLogSurface;
  /**
   * A BUILD-TIME production fact, never `NODE_ENV` on the server. See the note
   * at the top of this file — getting this wrong disables the dev droplet.
   */
  isProduction: boolean;
  /** Env var prefix for this bundler: "VITE_", "EXPO_PUBLIC_", "OMNIMEDIA_", "". */
  prefix: string;
  /** Which environment this build reports as. The Worker rejects anything else. */
  envName?: string;
  /** Session-scoped overrides (a query param or localStorage on web). */
  override?: { level?: string | null; ns?: string | null } | null;
}

export type DevLogDisabledReason =
  | "no-url"
  | "production"
  | "bad-url";

export interface DevLogConfig {
  enabled: boolean;
  /** "ok" when enabled; otherwise exactly why. Turns "why is it off" into one line. */
  reason: "ok" | DevLogDisabledReason;
  url: string | null;
  token: string | null;
  envName: string;
  minLevel: Level;
  filter: NamespaceFilter;
  persist: boolean;
  /** Flush when this many records are queued. */
  batchSize: number;
  /** Flush when queued bytes reach this. */
  batchBytes: number;
  flushMs: number;
  maxEntries: number;
  maxBytes: number;
  timeoutMs: number;
  /** Hard ceiling across all namespaces; past this everything is dropped. */
  saturationPerSec: number;
}

export const DEVLOG_DEFAULTS = Object.freeze({
  minLevel: LEVELS.trace as Level,
  ns: "*",
  batchSize: 100,
  batchBytes: 128_000,
  flushMs: 2000,
  maxEntries: 500,
  maxBytes: 1_000_000,
  timeoutMs: 5000,
  saturationPerSec: 200,
  envName: "dev",
});

function read(env: EnvRecord, prefix: string, name: string): string | null {
  const raw = env[`${prefix}DEVLOG_${name}`];
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readInt(
  env: EnvRecord,
  prefix: string,
  name: string,
  fallback: number,
): number {
  const raw = read(env, prefix, name);
  if (raw === null) return fallback;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback;
}

function disabled(
  reason: DevLogDisabledReason,
  host: HostFacts,
  env: EnvRecord,
): DevLogConfig {
  return {
    enabled: false,
    reason,
    url: null,
    token: null,
    envName: read(env, host.prefix, "ENV") ?? DEVLOG_DEFAULTS.envName,
    minLevel: DEVLOG_DEFAULTS.minLevel,
    filter: parseNamespaceFilter(""),
    persist: false,
    batchSize: DEVLOG_DEFAULTS.batchSize,
    batchBytes: DEVLOG_DEFAULTS.batchBytes,
    flushMs: DEVLOG_DEFAULTS.flushMs,
    maxEntries: DEVLOG_DEFAULTS.maxEntries,
    maxBytes: DEVLOG_DEFAULTS.maxBytes,
    timeoutMs: DEVLOG_DEFAULTS.timeoutMs,
    saturationPerSec: DEVLOG_DEFAULTS.saturationPerSec,
  };
}

/** Total and pure. Never throws, never reads a global. */
export function resolveDevLogConfig(
  env: EnvRecord,
  host: HostFacts,
): DevLogConfig {
  const url = read(env, host.prefix, "URL");
  if (url === null) return disabled("no-url", host, env);

  // The escape hatch requires a rebuild on every surface, which is the right
  // amount of friction for "yes, really ship debug logs from production".
  const allowProd = read(env, host.prefix, "ALLOW_PROD");
  if (host.isProduction && allowProd !== "1" && allowProd !== "true") {
    return disabled("production", host, env);
  }

  let normalized: string;
  try {
    const parsed = new URL(url);
    // A bare origin is the single most likely misconfiguration (the query CLI
    // takes the origin and appends /v1/* itself, so it is the URL people have
    // on their clipboard). Batches POSTed to "/" used to hit the Worker's
    // health route and count as delivered while storing nothing — observed
    // live on the desktop player window. Normalize it to the ingest endpoint
    // at this one choke point so every surface prefix is fixed at once.
    if (parsed.pathname === "/" || parsed.pathname === "") {
      parsed.pathname = "/v1/ingest";
    }
    normalized = parsed.toString();
  } catch {
    return disabled("bad-url", host, env);
  }

  const overrideLevel = host.override?.level ?? null;
  const overrideNs = host.override?.ns ?? null;

  return {
    enabled: true,
    reason: "ok",
    url: normalized,
    token: read(env, host.prefix, "TOKEN"),
    envName: read(env, host.prefix, "ENV") ?? DEVLOG_DEFAULTS.envName,
    minLevel: parseLevel(
      overrideLevel ?? read(env, host.prefix, "LEVEL"),
      DEVLOG_DEFAULTS.minLevel,
    ),
    filter: parseNamespaceFilter(
      overrideNs ?? read(env, host.prefix, "NS") ?? DEVLOG_DEFAULTS.ns,
    ),
    persist: read(env, host.prefix, "PERSIST") === "1",
    batchSize: readInt(env, host.prefix, "BATCH_SIZE", DEVLOG_DEFAULTS.batchSize),
    batchBytes: readInt(env, host.prefix, "BATCH_BYTES", DEVLOG_DEFAULTS.batchBytes),
    flushMs: readInt(env, host.prefix, "FLUSH_MS", DEVLOG_DEFAULTS.flushMs),
    maxEntries: readInt(env, host.prefix, "MAX_ENTRIES", DEVLOG_DEFAULTS.maxEntries),
    maxBytes: readInt(env, host.prefix, "MAX_BYTES", DEVLOG_DEFAULTS.maxBytes),
    timeoutMs: readInt(env, host.prefix, "TIMEOUT_MS", DEVLOG_DEFAULTS.timeoutMs),
    saturationPerSec: readInt(
      env,
      host.prefix,
      "SATURATION",
      DEVLOG_DEFAULTS.saturationPerSec,
    ),
  };
}
